import { useEffect } from 'react'
import { locationEvidenceService, type LocationActorRole } from '@lib/supabase/locationEvidence.service'

interface UseLocationEvidenceLoggerParams {
  role: LocationActorRole
  actorId?: string | null
}

export function useLocationEvidenceLogger({ role, actorId }: UseLocationEvidenceLoggerParams) {
  useEffect(() => {
    if (!actorId || typeof window === 'undefined') {
      return
    }

    let intervalId: number | null = null
    let isStopped = false

    const runLoggingCycle = async () => {
      if (isStopped || document.visibilityState !== 'visible') {
        return
      }

      await locationEvidenceService.logCurrentLocation({ role, actorId })
    }

    const startInterval = () => {
      if (intervalId) return
      intervalId = window.setInterval(() => {
        void runLoggingCycle()
      }, locationEvidenceService.intervalMs)
    }

    const stopInterval = () => {
      if (!intervalId) return
      window.clearInterval(intervalId)
      intervalId = null
    }

    const initialize = async () => {
      const permissionGranted = await locationEvidenceService.ensureForegroundPermission()
      if (!permissionGranted) {
        return
      }

      await runLoggingCycle()
      startInterval()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runLoggingCycle()
        startInterval()
      } else {
        stopInterval()
      }
    }

    void initialize()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleVisibilityChange)
    window.addEventListener('blur', handleVisibilityChange)

    return () => {
      isStopped = true
      stopInterval()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleVisibilityChange)
      window.removeEventListener('blur', handleVisibilityChange)
    }
  }, [role, actorId])
}